import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";

interface ModalViewImagesAnswersProps {
  open: boolean;
  handleClose: () => void;
  images: any[];
  loading: boolean;
  title: string;
  translate: (key: string) => string;
}

const ModalViewImagesAnswers: React.FC<ModalViewImagesAnswersProps> = ({
  open,
  handleClose,
  images,
  loading,
  title,
  translate,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
    null
  );
  const [zoomLevel, setZoomLevel] = useState(1);

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    setZoomLevel(1);
  };

  const handleCloseFullscreen = () => {
    setSelectedImageIndex(null);
    setZoomLevel(1);
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.2, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.2, 0.5));
  };

  const handleNextImage = () => {
    if (selectedImageIndex !== null && images.length > 0) {
      setSelectedImageIndex((selectedImageIndex + 1) % images.length);
      setZoomLevel(1);
    }
  };

  const handlePrevImage = () => {
    if (selectedImageIndex !== null && images.length > 0) {
      setSelectedImageIndex(
        (selectedImageIndex - 1 + images.length) % images.length
      );
      setZoomLevel(1);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreen}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {title}
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 110,
                flexDirection: "column",
              }}
            >
              <CircularProgress color="success" />
              <Typography variant="caption" display="block">
                {translate("defaultMessages_loading")}
              </Typography>
            </Box>
          ) : (
            <Grid
              container
              spacing={2}
              sx={{ maxHeight: "60vh", overflow: "auto", padding: 5 }}
            >
              {images.map((image, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 1,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      cursor: "pointer",
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "scale(1.02)",
                      },
                    }}
                    onClick={() => handleImageClick(index)}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        overflow: "hidden",
                        borderRadius: 1,
                        mb: 1,
                        maxHeight: 150,

                      }}
                    >
                      <img
                        src={image.tempUri}
                        alt={image.name || `Imagem ${index + 1}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      display="block"
                      align="center"
                      sx={{
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {image.name || `Imagem ${index + 1}`}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="error" variant="contained">
            {translate("buttons_close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Visualização em tela cheia */}
      {selectedImageIndex !== null && (
        <Dialog
          open={selectedImageIndex !== null}
          onClose={handleCloseFullscreen}
          fullScreen
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0,0,0,0.9)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                p: 2,
                color: "white",
              }}
            >
              <Typography variant="h6">
                {images[selectedImageIndex]?.name ||
                  `Imagem ${selectedImageIndex + 1} de ${images.length}`}
              </Typography>
              <Box>
                <IconButton onClick={handleZoomOut} color="inherit">
                  <ZoomOutIcon />
                </IconButton>
                <IconButton onClick={handleZoomIn} color="inherit">
                  <ZoomInIcon />
                </IconButton>
                <IconButton onClick={handleCloseFullscreen} color="inherit">
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
              }}
            >
              <IconButton
                onClick={handlePrevImage}
                sx={{
                  position: "absolute",
                  left: 16,
                  color: "white",
                  backgroundColor: "rgba(0,0,0,0.3)",
                }}
              >
                <NavigateBeforeIcon fontSize="large" />
              </IconButton>

              <Box
                sx={{
                  maxWidth: "90%",
                  maxHeight: "80%",
                  overflow: "auto",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <img
                  src={images[selectedImageIndex]?.tempUri}
                  alt={
                    images[selectedImageIndex]?.name ||
                    `Imagem ${selectedImageIndex + 1}`
                  }
                  style={{
                    transform: `scale(${zoomLevel})`,
                    transition: "transform 0.2s ease-in-out",
                    maxWidth: "100%",
                    maxHeight: "100%",
                  }}
                />
              </Box>

              <IconButton
                onClick={handleNextImage}
                sx={{
                  position: "absolute",
                  right: 16,
                  color: "white",
                  backgroundColor: "rgba(0,0,0,0.3)",
                }}
              >
                <NavigateNextIcon fontSize="large" />
              </IconButton>
            </Box>
          </Box>
        </Dialog>
      )}
    </>
  );
};
export default ModalViewImagesAnswers;
